import {
  Alert,
  Box, CircularProgress, FormControl, FormControlLabel,
  Radio, RadioGroup, Snackbar, Typography, useTheme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingStatus } from '../../../common/enums';
import { CustomButton } from './common/CustomHDSetupButton';
import { updateMemberToPartner } from '../../../store/thunks/dashboardThunk';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectMemberSession, updateNavigationFromOnboarding } from '../../../store/slices/onboardingSlice';
import { TestIds } from '../../../assets/testIds';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useOnboardingRedirectPath } from '../../../common/useOnboardingRedirectPath';
import { isUserInitiatedHomeDepotEnrollment } from '../../../common/homeDepotEnrollmentIntent';

interface IFormInputs {
  shoppingStatus: ShoppingStatus | null;
}

interface OnboardHDShopQuestionProps {
  setShowHDShopQuestion: (value: boolean) => void,
}
export function OnboardHDShopQuestion({ setShowHDShopQuestion }: OnboardHDShopQuestionProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { memberId } = useAppSelector(selectMemberSession);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ShoppingStatus | null>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPartnerEnrollment, trackHomeDepotEnrollmentStart } = useCustomEventTracking();
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const {
    register, handleSubmit, control, formState,
  } = useForm<IFormInputs>({ mode: 'onChange' });

  // get redirect path on optimizely decision
  const redirectPath = useOnboardingRedirectPath({
    redirectToPartnersForOnboardingFlowTestOff: true,
  });

  // Handle the radio button change
  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(e.target.value as ShoppingStatus);
  };

  const onSubmit = (data: IFormInputs) => {
    if (data.shoppingStatus) {
      setLoading(true);
      dispatch(updateMemberToPartner({
        memberId,
        partnerId: process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '',
        data: { shoppingStatus: data.shoppingStatus },
      }))
        .then(unwrapResult)
        .then(() => {
          setOpenErrorSnackbar(false);

          // fire shopping status event
          trackPartnerEnrollment('Submit', 'HomeDepotShoppingStatus', {
            shoppingStatus: data.shoppingStatus || 'unknown',
          });

          if (data.shoppingStatus === ShoppingStatus.NotShopping) {
            const userInitiatedHdEnrollment = isUserInitiatedHomeDepotEnrollment({
              state: location.state,
              search: location.search,
            });

            if (!userInitiatedHdEnrollment) {
              dispatch(updateNavigationFromOnboarding(true));
            }
            // navigate to content home page or rewards page
            navigate(redirectPath, {
              state: { fromOnboarding: true },
            });
          } else {
            // show the hd setup instructions
            setShowHDShopQuestion(false);
            trackHomeDepotEnrollmentStart();
          }
        })
        .catch((error: unknown) => {
          setOpenErrorSnackbar(true);
          setLoading(false);
        });
    }
  };

  const handleErrorClose = () => {
    setOpenErrorSnackbar(false);
  };

  return (
    <Box>
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleErrorClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <Controller
            name="shoppingStatus"
            control={control}
            defaultValue={null}
            rules={{
              required: 'Please select an option', // Validation message
            }}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                aria-label="shopping status"
                row
                value={value}
                onChange={(e) => {
                  onChange(e);
                  handleRadioChange(e); // Call the handler on change
                }}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                {
                  [
                    {
                      value: ShoppingStatus.Shopping,
                      label: 'Yes',
                      id: TestIds.onboarding.homedepotShopYesBtn,
                      isDisabled: loading,
                    },
                    {
                      value: ShoppingStatus.NotShopping,
                      label: 'No',
                      id: TestIds.onboarding.homedepotShopNoBtn,
                      isDisabled: loading,
                    },
                  ].map((option, indx) => (
                    <FormControlLabel
                      key={indx}
                      data-testid={option.id}
                      id={option.id}
                      value={option.value}
                      control={
                        <Radio
                          disabled={option.isDisabled} // Conditionally disable
                          sx={{
                            display: 'none', // Hide default radio button circle
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            flex: 1, // Makes each button take equal width
                            border: `1px solid ${theme.palette.grey[200]}`,
                            borderRadius: theme.spacing(1.5),
                            padding: theme.spacing(1.875),
                            textAlign: 'center',
                            borderColor: (value === option.value ?
                              theme.palette.brand.primary :
                              theme.palette.grey[200]),
                            '&:hover': option.isDisabled ?
                              {} :
                              {
                                backgroundColor: theme.palette.brand.surface,
                                borderColor: theme.palette.brand.primary,
                              },
                            cursor: 'pointer', // Cursor like a button
                            transition: 'border-color 0.3s',
                          }}
                        >
                          {option.label}
                        </Typography>
                      }
                      {...register('shoppingStatus')}
                      sx={{
                        flex: 1,
                        mr: indx + 1 !== 2 ? theme.spacing(1.25) : 0,
                        ml: 0,
                      }}
                    />
                  ))
                }
              </RadioGroup>
            )} />
        </FormControl>
        <CustomButton
          type={'submit'}
          btnText={
            loading ?
              <CircularProgress color="inherit" size={24} /> :
              <Typography component='span' data-testid={TestIds.onboarding.homedepotShopNextBtn} sx={{ fontWeight: 600 }}>
                {selectedOption === ShoppingStatus.NotShopping ?
                  t('button.completeHdWebSetup') :
                  t('button.next')
                }
              </Typography>
          }
          disabled={!formState.isValid || loading}
        />
      </form>
    </Box>
  );
}
